import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "./localStorage";

/**
 * 直前に使っていたテナントの記憶（PRODUCT_DEV-1129）
 *
 * ## なぜ要るのか
 *
 * セッションが切れて認証ポータルへ出ると、所属が複数あるユーザは毎回テナントを
 * 選び直させられていた。ポータルへ出す URL に `lastTenantId` を付けると、ポータルは
 * そのテナントに所属していれば選択画面を飛ばす（所属していなければ普通の選択画面。
 * 「入りたい」という希望であって、`tenantId` のような絞り込みではない）。
 *
 * ## なぜ localStorage か
 *
 * 翌日に新しいタブで開いたとき・ホーム画面のアプリを開き直したときにも効かせるため
 * （sessionStorage はタブを閉じると消える）。ログアウトでは消す。
 *
 * 30 日を過ぎた値は使わない。テナントから外された人が古いテナントを指し続けないため
 * （外れていてもポータルは選択画面を出すので詰まりはしない）。
 */

/** 記憶の寿命。 */
export const LAST_TENANT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** プロダクトごとのキー。トークン保存名が違えば混ざらない。 */
export const lastTenantKey = (tokenStoreName: string): string =>
  `${tokenStoreName}:lastTenant`;

export const saveLastTenant = (
  key: string,
  tenantId: string,
  now: number = Date.now(),
): void => {
  if (!tenantId) return;
  setLocalStorageItem(key, { tenantId, savedAt: now });
};

/** 期限切れ・壊れた値は `null`。 */
export const readLastTenant = (
  key: string,
  now: number = Date.now(),
): string | null => {
  const parsed = getLocalStorageItem<unknown>(key, null);
  if (typeof parsed !== "object" || parsed === null) return null;
  const { tenantId, savedAt } = parsed as {
    tenantId?: unknown;
    savedAt?: unknown;
  };
  if (typeof tenantId !== "string" || !tenantId) return null;
  if (typeof savedAt !== "number" || now - savedAt > LAST_TENANT_TTL_MS) {
    return null;
  }
  return tenantId;
};

/** ログアウト時に呼ぶ（残すと、同じブラウザで次にログインした人へ前の人のテナントを勧める）。 */
export const clearLastTenant = (key: string): void => {
  removeLocalStorageItem(key);
};
